import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Card, Spin, Button, message, Space, Tooltip } from 'antd';
import {
  ArrowLeftOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  DeleteOutlined,
  PlusOutlined,
  FileExcelOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { CreditCardResponse2 } from '../model/Credit-card.model';
import { DeletedMaturityScheduleResponse } from '../model/DeletedMaturitySchedule.model';
import CreditCardService from '../service/credit-card-service/Credit-card.service';
import DeletedMaturityScheduleService from '../service/maturity-schedule-service/DeletedMaturitySchedule.service';
import CreditCardInfo from '../component/card-detail/CreditCardInfo';
import MaturityScheduleList from '../component/card-detail/MaturityScheduleList';
import DeletedMaturityScheduleTable from '../component/card-detail/DeletedMaturityScheduleTable';
import CardMultiExportModal from '../component/card-detail/CardMultiExportModal';
import CreateMaturityScheduleModal from '../component/card-detail/CreateMaturityScheduleModal';
import WithdrawalTransactionList from '../component/card-detail/WithdrawalTransactionList';
import DeletedWithdrawalTransactionList from '../component/card-detail/DeletedWithdrawalTransactionList';
import { useCardDetail } from '../context/CardDetailContext';

const { TabPane } = Tabs;

const CardDetail: React.FC = () => {
  const { selectedCard, maturityScheduleId, maturityYear } = useCardDetail();
  const navigate = useNavigate();
  const maturityTableRef = useRef<HTMLDivElement>(null);
  const [creditCard, setCreditCard] = useState<CreditCardResponse2 | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('active');
  const [deletedSchedules, setDeletedSchedules] = useState<
    DeletedMaturityScheduleResponse[]
  >([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [maturitySchedules, setMaturitySchedules] = useState<any[]>([]);

  // Year state management for each tab
  const [maturityScheduleYear, setMaturityScheduleYear] = useState(
    maturityYear || new Date().getFullYear()
  );
  const [deletedMaturityScheduleYear, setDeletedMaturityScheduleYear] =
    useState(new Date().getFullYear());
  const [withdrawalTransactionYear, setWithdrawalTransactionYear] = useState(
    new Date().getFullYear()
  );
  const [
    deletedWithdrawalTransactionYear,
    setDeletedWithdrawalTransactionYear,
  ] = useState(new Date().getFullYear());

  useEffect(() => {
    if (!selectedCard) {
      navigate(-1);
      return;
    }

    loadCreditCard();
    loadDeletedSchedules();

    // Auto-scroll to maturity table if maturityScheduleId exists
    if (maturityScheduleId && maturityTableRef.current) {
      setTimeout(() => {
        maturityTableRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, [selectedCard]);

  const loadCreditCard = async () => {
    if (!selectedCard?.id) return;

    try {
      setLoading(true);
      const card = await CreditCardService.getCreditCardById(selectedCard.id);
      setCreditCard(card);
    } catch (error) {
      message.error('Không thể tải thông tin thẻ tín dụng');
      console.error('Error loading credit card:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedSchedules = async () => {
    if (!selectedCard?.id) return;

    try {
      setDeletedLoading(true);
      const deleted =
        await DeletedMaturityScheduleService.getDeletedMaturitySchedulesByYearAndCard(
          deletedMaturityScheduleYear,
          selectedCard.id
        );
      setDeletedSchedules(deleted);
    } catch (error) {
      console.error('Error loading deleted maturity schedules:', error);
      message.error('Không thể tải danh sách kỳ đáo hạn đã xóa');
    } finally {
      setDeletedLoading(false);
    }
  };

  // Reload deleted schedules when year changes
  useEffect(() => {
    if (selectedCard?.id && activeTab === 'deleted') {
      loadDeletedSchedules();
    }
  }, [deletedMaturityScheduleYear, selectedCard?.id, activeTab]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);

    // Load data when switching to specific tabs
    if (key === 'deleted') {
      loadDeletedSchedules();
    }
  };

  const handleCreateSchedule = () => {
    setShowCreateModal(true);
  };

  // Check if create button should be disabled
  const isCreateDisabled = () => {
    // If card is disabled (KHONG_LAM), disable creation completely
    return creditCard?.newStatus === 'KHONG_LAM';
  };

  const getCreateButtonTooltip = () => {
    if (creditCard?.newStatus === 'KHONG_LAM') {
      return 'Không thể tạo kỳ đáo hạn mới cho thẻ đã vô hiệu hóa';
    }
    return 'Tạo kỳ đáo hạn mới';
  };

  const handleExportExcel = () => {
    setShowExportModal(true);
  };

  const handleSchedulesUpdate = (
    schedules: any[],
    month: number,
    year: number
  ) => {
    setMaturitySchedules(schedules);
  };

  // Nếu không có selectedCard (truy cập trực tiếp), chuyển về trang trước
  useEffect(() => {
    if (!selectedCard) {
      navigate(-1); // hoặc navigate('/credit-cards');
    }
  }, [selectedCard, navigate]);

  if (!selectedCard) {
    return null; // hoặc loading/error UI
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!creditCard) {
    return (
      <div className="p-6">
        <Card>
          <div className="text-center">
            <p>Không tìm thấy thông tin thẻ tín dụng</p>
            <Button onClick={handleBack}>Quay lại</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center space-x-4 mb-6">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Quay lại
        </Button>
        <h1 className="text-2xl font-bold text-gray-800 m-0">
          Chi tiết thẻ tín dụng:{' '}
          {selectedCard?.cardNumber || creditCard.cardNumber}
        </h1>
      </div>

      {/* Basic Card Information */}
      <div className="mb-6">
        <CreditCardInfo creditCard={creditCard} onRefresh={loadCreditCard} />
      </div>

      {/* Maturity Schedules */}
      <Card className="shadow-md" ref={maturityTableRef}>
        <Tabs activeKey={activeTab} onChange={handleTabChange} className="mt-0">
          <TabPane
            tab={
              <span>
                <CalendarOutlined />
                Kỳ đáo hạn hiện tại
              </span>
            }
            key="active"
          >
            {/* Action Buttons */}
            <div className="mb-6 flex justify-end">
              <Space>
                <Button
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 text-white border-green-600 hover:border-green-700"
                >
                  Xuất Excel
                </Button>
                <Tooltip title={getCreateButtonTooltip()}>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateSchedule}
                    size="middle"
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={isCreateDisabled()}
                  >
                    Tạo kỳ đáo hạn mới
                  </Button>
                </Tooltip>
              </Space>
            </div>

            {/* Active Schedules Table */}
            <MaturityScheduleList
              cardId={selectedCard?.id || 0}
              onSchedulesUpdate={handleSchedulesUpdate}
              cardInfo={{
                cardNumber: selectedCard?.cardNumber || '',
                cardHolderName: selectedCard?.cardHolderName || '',
                customerName: selectedCard?.customer?.name || '',
                bankName: selectedCard?.bank?.name || '',
                cardType: selectedCard?.cardType?.name || '',
                newStatus: selectedCard?.newStatus,
              }}
              initialFilters={{
                maturityScheduleId: maturityScheduleId,
                year: maturityYear,
              }}
              selectedYear={maturityScheduleYear}
              onYearChange={setMaturityScheduleYear}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <DeleteOutlined />
                Kỳ đáo hạn đã xóa
              </span>
            }
            key="deleted"
          >
            <div className="mb-4">
              <p className="text-gray-600 text-sm">
                Danh sách các kỳ đáo hạn đã bị xóa cho thẻ này. Khi mở rộng, bạn
                có thể xem các giao dịch và thanh toán liên quan đã bị xóa.
              </p>
            </div>

            {/* Deleted Schedules Table */}
            <DeletedMaturityScheduleTable
              deletedSchedules={deletedSchedules}
              loading={deletedLoading}
              selectedYear={deletedMaturityScheduleYear}
              onYearChange={setDeletedMaturityScheduleYear}
              onRefresh={loadDeletedSchedules}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <WalletOutlined />
                Giao dịch rút tiền
              </span>
            }
            key="withdrawal"
          >
            {/* Active Withdrawal Transactions */}
            <WithdrawalTransactionList
              creditCardId={selectedCard?.id || 0}
              onRefresh={() => {
                // Optional: refresh other data if needed
                loadCreditCard();
              }}
              cardInfo={{
                cardNumber: selectedCard?.cardNumber || '',
                cardHolderName: selectedCard?.cardHolderName || '',
                customerName: selectedCard?.customer?.name || '',
                bankName: selectedCard?.bank?.name || '',
                cardType: selectedCard?.cardType?.name || '',
                newStatus: selectedCard?.newStatus,
              }}
              selectedYear={withdrawalTransactionYear}
              onYearChange={setWithdrawalTransactionYear}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <DeleteOutlined />
                Rút tiền đã xóa
              </span>
            }
            key="withdrawal-deleted"
          >
            {/* Deleted Withdrawal Transactions */}
            <DeletedWithdrawalTransactionList
              creditCardId={selectedCard?.id || 0}
              selectedYear={deletedWithdrawalTransactionYear}
              onYearChange={setDeletedWithdrawalTransactionYear}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Export Modal */}
      <CardMultiExportModal
        visible={showExportModal}
        onClose={() => setShowExportModal(false)}
        maturitySchedules={maturitySchedules}
        cardInfo={{
          cardNumber: selectedCard?.cardNumber || '',
          cardHolderName: selectedCard?.cardHolderName || '',
          customerName: selectedCard?.customer?.name || '',
          bankName: selectedCard?.bank?.name || '',
          cardType: selectedCard?.cardType?.name || '',
        }}
        selectedYear={maturityScheduleYear}
      />

      {/* Create Modal */}
      <CreateMaturityScheduleModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          // Trigger refresh by calling loadCreditCard
          loadCreditCard();
        }}
        cardId={selectedCard?.id || 0}
      />
    </div>
  );
};

export default CardDetail;
